import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {DUST_DEVIL_KIND} from '../../../configs/biomeEffects/dustDevil';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  hash01,
  lerp,
  pickPointInBox,
  randomIn,
  randomRange,
  randomSign,
  resolveOvalFade,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ПЫЛЕВОЙ ВИХРЬ (красная подложка). Чистые функции без Phaser и DOM: по этому
 * описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Вихрь — группа из пяти видов частиц, и все они привязаны к одному пути:
 *  - путь — прямая по ветру матча (с отклонением) плюс две волны поперек нее.
 *    Положение вихря — функция времени, поэтому и шлейф считается без памяти;
 *  - ТЕЛО ВИХРЯ — НЕ РОВНЫЙ ДИСК, оно все время меняет очертание (просьба
 *    владельца игры: прежде вихрь был «все время одной и той же формы»):
 *     · ядро: светлый клуб в середине вихря; вращается, а ширина и высота его
 *       кадра дышат порознь, каждая — парой волн с несоизмеримыми периодами;
 *     · доли: клубы поменьше кружат вокруг середины вихря, каждая — на своем
 *       расстоянии и в своем темпе. Плотное место тела там, где доли легли
 *       друг на друга: оно гуляет по вихрю, и его край то и дело выпирает в
 *       новую сторону;
 *     · пряди: клочья пыли, которые вихрь срывает с тела. Прядь рождается у
 *       тела, уходит от него по раскручивающейся спирали (вихрь несет ее по
 *       кругу, а она отдаляется), вытягивается, становится тоньше и ТАЕТ К
 *       КРАЮ: плотность пряди — убывающая функция расстояния до середины
 *       вихря, у внешнего радиуса она ноль. Потом прядь рождается снова — у
 *       тела, под другим углом. У каждой пряди свой срок круга и своя очередь:
 *       рождаются они вразнобой, и двух одинаковых кадров у вихря нет.
 *    Вращение одно на всех: ядро, доли и пряди крутятся в одну сторону. Кольца
 *    нет: оно читалось бы меткой на ячейке; спиц тоже — прядь лежит вдоль
 *    своей спирали, почти по касательной к вихрю. Сама прядь — мягкая дуга,
 *    выгнутая наружу: она огибает тело;
 *  - шлейф: клуб рождается под вихрем и остается там, где вихрь был в миг его
 *    рождения, — растет, редеет и понемногу сносится ветром, потом рождается
 *    снова. Клубов много, и лежат они внахлест: за вихрем тянется сплошная
 *    полоса пыли. ПЫЛЬ ВИСИТ ДОЛГО И ПЕРЕЖИВАЕТ ВИХРЬ (просьба владельца игры:
 *    «это ж смерч, после него не сразу пыль пропадает»): вихрь сошел на нет, а
 *    полоса за ним доживает свой срок и тает сама;
 *  - тень столба — короткое размытое пятно-воронка: темнее всего под вихрем,
 *    оно расходится и тает в стороне, куда на картинке пейзажа падают тени.
 *    Тень покачивается вокруг основания вместе со столбом.
 *
 * ВИХРЬ РАСТЕТ И СХОДИТ НА НЕТ РАЗМЕРОМ ВМЕСТЕ С ПЛОТНОСТЬЮ. Рождается он
 * маленьким и прозрачным и плавно вырастает до полного размера; в конце так же
 * плавно сжимается к своей середине, пока плотность уходит в ноль, — а не
 * стоит в полный рост, становясь прозрачным. Размер и плотность ведет одна и
 * та же плавная кривая: глаз видит одно движение. Ядро, доли, пряди и тень в
 * любой миг сжаты одинаково; клубы шлейфа, уже легшие на путь, живут сами по
 * себе — лежат где легли, растут и тают.
 *
 * ГРУППА ЖИВЕТ ДОЛЬШЕ ВИХРЯ: вихрь — первые whirlMs ее жизни, дальше на поле
 * один шлейф, пока не растает последний его клуб (durationMs). Поэтому в
 * расписании дорожки maxGroups — число групп, а не вихрей: новому вихрю
 * доживающий шлейф прежнего не помеха.
 *
 * У ВИХРЯ ЕСТЬ ЗВУК (см. configs/biomeEffects/dustDevil: DUST_DEVIL_SOUND_ID).
 * Дорожка вихря помечена (DUST_DEVIL_TAG): по метке сцена не дает вихрю
 * родиться, пока звук не загружен, и находит идущий вихрь, а слышимость звука
 * ведет по его силе (resolveDustDevilStrength) — той же, что ведет плотность
 * тела. Сила — про сам вихрь: шлейф, который его пережил, не звучит. Группа с
 * меткой жива и после конца вихря — пока висит шлейф: идет ли вихрь, говорит
 * только его сила, а не то, что группа нашлась.
 *
 * Вихрь ходит только по ровному дну котловины: у его кромки гаснет. Идет он
 * над всем полем — над жетонами, отрядами и их значками (слой AIR).
 */

const CORE = 'core';
const LOBE = 'lobe';
const WISP = 'wisp';
const PLUME = 'plume';
const SHADOW = 'shadow';

/** Метка дорожки вихря: по ней сцена находит идущий вихрь и решает, можно ли ему родиться */
export const DUST_DEVIL_TAG = 'dustDevil';

// Порядок внутри слоя: тень — ниже всего, над ней шлейф, тело (ядро и доли) и пряди
const SUBLAYERS = {[SHADOW]: 0, [PLUME]: 1, [CORE]: 2, [LOBE]: 2, [WISP]: 3};

// Очереди прядей разложены по кругу жизни золотым сечением: сколько бы прядей
// ни было, соседние очереди не совпадают и не сбиваются в кучу
const GOLDEN_SHARE = (Math.sqrt(5) - 1) / 2;

/** Насколько точка далека от середины овала: 0 — середина, 1 — кромка */
const resolveOvalDistance = (oval, x, y) => Math.hypot((x - oval.x) / oval.radiusX, (y - oval.y) / oval.radiusY);

/** Сумма волн в момент времени: у каждой свой размах (share), период и начальная точка */
const sumWaves = (waves, timeMs) => {
  let total = 0;
  for (let index = 0; index < waves.length; index += 1) {
    const wave = waves[index];
    total += wave.share * Math.sin((TWO_PI * timeMs) / wave.periodMs + wave.phase);
  }
  return total;
};

// Точка пути пишется в один и тот же объект: расчет идет каждый кадр
const point = {x: 0, y: 0};
/** Где вихрь в момент времени (пишет в общий объект point) */
const resolveDevilPoint = (group, timeMs) => {
  const along = (group.speed * timeMs) / 1000;
  let lateral = 0;
  for (let index = 0; index < group.meanders.length; index += 1) {
    const meander = group.meanders[index];
    lateral += meander.amplitude * Math.sin((TWO_PI * timeMs) / meander.periodMs + meander.phase);
  }
  point.x = group.start.x + group.heading.x * along - group.heading.y * lateral;
  point.y = group.start.y + group.heading.y * along + group.heading.x * lateral;
  return point;
};

// Жизнь вихря: насколько он проступил с рождения и сколько его осталось перед
// концом. Одна и та же плавная кривая ведет и плотность, и размер. Конец — у
// самого вихря (whirlMs), а не у группы: группа доживает со шлейфом
const resolveAppear = (group, timeMs) => smoothstep(0, group.growMs, timeMs);
const resolveVanish = (group, timeMs) => smoothstep(0, group.fadeMs, group.whirlMs - timeMs);

/**
 * Сила вихря в момент времени: 0 — его нет (не родился, кончился или ушел за
 * кромку дна котловины), 1 — стоит на дне в полный рост. Плавно нарастает, пока
 * вихрь растет, и плавно спадает, пока он сходит на нет. Это та самая доля, на
 * которую умножена плотность ядра, долей, прядей и тени: по ней сцена ведет
 * слышимость звука вихря — звук и картинка идут в лад.
 *
 * Сила — про сам вихрь, а не про его шлейф: вихрь кончился (whirlMs) — сила
 * ноль, хотя группа еще жива и пыль за вихрем еще висит.
 *
 * @param {Object} group — группа вихря (из buildGroup его дорожки)
 * @param {number} elapsedMs — сколько прошло с рождения вихря
 * @returns {number} 0..1
 */
export const resolveDustDevilStrength = (group, elapsedMs) => {
  if (!(elapsedMs > 0) || !(elapsedMs < group.whirlMs)) {
    return 0;
  }
  const {x, y} = resolveDevilPoint(group, elapsedMs);
  // На гребни вокруг дна котловины вихрь не заходит: у кромки гаснет целиком
  return resolveAppear(group, elapsedMs) * resolveVanish(group, elapsedMs)
    * resolveOvalFade(group.floor, x, y, group.floorFade);
};

const createTracks = (context, params) => {
  const {hexSize, wind} = context;
  const basin = context.toWorldOval(params.basin);
  const windAngle = Math.atan2(wind.y, wind.x);
  const shadowAngle = toRadians(params.shadow.angleDeg);
  // Сколько живет клуб шлейфа — и на сколько шлейф переживает вихрь
  const plumeLifeMs = params.plume.count * params.plume.lagMs;
  // Спираль пряди — логарифмическая: угол растет с логарифмом расстояния до
  // середины вихря. На столько логарифм вырастает за круг жизни пряди
  const wispSpiralSpan = Math.log(params.wisps.radiusShare.to / params.wisps.radiusShare.from);
  // Кадр тени длиннее самой тени: основание воронки в нем — не у края (слева
  // от основания — запас, на котором тень сходит на нет). Во сколько раз кадр
  // длиннее тени и на какой доле этой длины от вихря лежит середина кадра
  const shadowFrameScale = 1 / (params.shadow.frame.endShare - params.shadow.frame.baseShare);
  const shadowCentreShare = (0.5 - params.shadow.frame.baseShare) * shadowFrameScale;

  /**
   * Дно для вихря такого размера: овал дна котловины, ужатый на размах тела
   * вихря. Вихрь — не точка: у кромки дна обязано гаснуть не только его
   * середина, но и край пятна, иначе крупный вихрь лег бы краем на гребни
   */
  const resolveFloor = (coreSize) => {
    const reach = coreSize * params.floor.bodyReachShare;
    return {
      x: basin.x,
      y: basin.y,
      radiusX: Math.max(basin.radiusX * params.floor.minShare, basin.radiusX - reach),
      radiusY: Math.max(basin.radiusY * params.floor.minShare, basin.radiusY - reach),
    };
  };

  /** Точка, придвинутая к середине дна, если она у его кромки или за ней */
  const pullOntoFloor = (floor, place) => {
    const distance = resolveOvalDistance(floor, place.x, place.y);
    if (distance <= params.basinInnerShare) {
      return place;
    }
    const share = params.basinInnerShare / distance;
    return {x: floor.x + (place.x - floor.x) * share, y: floor.y + (place.y - floor.y) * share};
  };

  /**
   * Где может быть середина пути вихря: поле в кадре, отступив от его краев, —
   * иначе вихрь полжизни бродил бы за кадром. Кадр уже двух отступов —
   * остается его середина
   */
  const resolveThroughBox = (view) => {
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0});
    const insetX = Math.min(hexSize * params.viewInsetFactor, (box.right - box.left) / 2);
    const insetY = Math.min(hexSize * params.viewInsetFactor, (box.bottom - box.top) / 2);
    return {left: box.left + insetX, right: box.right - insetX, top: box.top + insetY, bottom: box.bottom - insetY};
  };

  /** Волны с начальными точками этого вихря: у каждого вихря тело дышит по-своему */
  const buildWaves = (rng, waves) => waves.map(({share, periodMs}) => ({share, periodMs, phase: rng() * TWO_PI}));

  const buildGroup = ({rng, view}) => {
    const whirlMs = randomIn(rng, params.lifeMs);
    const speed = hexSize * randomIn(rng, params.speedFactor);
    const halfPath = (speed * whirlMs) / 1000 / 2;
    const coreSize = hexSize * randomIn(rng, params.core.sizeFactor);
    const floor = resolveFloor(coreSize);
    // Середина пути — там, куда смотрит камера. Из нескольких путей берется
    // тот, чьи концы глубже всего на дне котловины: вихрь не тратит жизнь на
    // гребнях, где его не видно
    const box = resolveThroughBox(view);
    let path = null;
    for (let attempt = 0; attempt < params.pathAttempts; attempt += 1) {
      const through = pullOntoFloor(floor, pickPointInBox(rng, box));
      const angle = windAngle + toRadians(randomRange(rng, -params.headingSpreadDeg, params.headingSpreadDeg));
      const heading = {x: Math.cos(angle), y: Math.sin(angle)};
      const start = {x: through.x - heading.x * halfPath, y: through.y - heading.y * halfPath};
      const reach = Math.max(
        resolveOvalDistance(floor, start.x, start.y),
        resolveOvalDistance(floor, through.x + heading.x * halfPath, through.y + heading.y * halfPath)
      );
      if (!path || reach < path.reach) {
        path = {start, heading, reach};
      }
    }

    const spinDirection = randomSign(rng);
    const meanders = params.meanders.map(({amplitudeFactor, periodMs}) => ({
      amplitude: hexSize * randomIn(rng, amplitudeFactor),
      periodMs: randomIn(rng, periodMs),
      phase: rng() * TWO_PI,
    }));

    const particles = [];
    const addParticle = (kind, texture, tint, fields) => particles.push({
      kind, layer: FIELD_BIOME_LAYER_KEYS.AIR, sublayer: SUBLAYERS[kind], texture, tint, ...fields,
    });
    addParticle(SHADOW, FIELD_BIOME_TEXTURES.FUNNEL, params.shadow.tint);
    for (let index = 0; index < params.plume.count; index += 1) {
      addParticle(PLUME, FIELD_BIOME_TEXTURES.PUFF, params.plume.tint, {
        // Зерно клуба: по нему и номеру круга выбирается сдвиг с пути и поворот
        seed: 1 + index + rng(),
        // Клубы рождаются по очереди, через равные промежутки
        phaseMs: index * params.plume.lagMs,
      });
    }
    addParticle(CORE, FIELD_BIOME_TEXTURES.PUFF, params.core.tint, {
      spinPhase: rng() * TWO_PI,
      widthWaves: buildWaves(rng, params.core.stretch.width),
      heightWaves: buildWaves(rng, params.core.stretch.height),
    });
    params.lobes.forEach(({sizeShare, orbitShare, orbitPeriodMs, spinPeriodMs, alpha}) => {
      addParticle(LOBE, FIELD_BIOME_TEXTURES.PUFF, params.core.tint, {
        sizeShare, orbitShare, orbitPeriodMs, spinPeriodMs, alpha,
        // С какого места своего круга доля начинает и как повернут ее клуб
        orbitPhase: rng() * TWO_PI,
        spinPhase: rng() * TWO_PI,
      });
    });
    const wispQueueShift = rng();
    for (let index = 0; index < params.wisps.count; index += 1) {
      // Срок круга у каждой пряди свой: сроки разложены по всему отрезку, двух
      // одинаковых нет — пряди, родившиеся разом, тут же расходятся
      const cycleMs = lerp(params.wisps.cycleMs.min, params.wisps.cycleMs.max, (index + rng()) / params.wisps.count);
      const turns = randomIn(rng, params.wisps.turns);
      addParticle(WISP, FIELD_BIOME_TEXTURES.RIM_SEGMENT, params.wisps.tint, {
        // Зерно пряди: по нему и номеру круга выбирается угол, под которым она рождается
        seed: 1 + index + rng(),
        cycleMs,
        // Какую долю своего круга прядь уже прошла к рождению вихря — ее очередь
        phaseMs: cycleMs * ((wispQueueShift + index * GOLDEN_SHARE) % 1),
        turns,
        // Прядь лежит вдоль своей спирали: на столько спираль отклонена от
        // касательной к кругу вокруг середины вихря — наружу. У логарифмической
        // спирали этот угол один на всем ее протяжении, и он мал: прядь не
        // встает спицей ни у тела, ни у края
        pitch: Math.atan(wispSpiralSpan / (TWO_PI * turns)),
      });
    }

    return {
      // Группа живет, пока не растает последний клуб шлейфа: самый поздний
      // рождается под конец вихря и живет свой срок
      durationMs: whirlMs + plumeLifeMs,
      // Сколько живет сам вихрь — вместе с ростом в начале и угасанием в конце
      whirlMs,
      start: path.start, heading: path.heading, speed, meanders, coreSize, spinDirection, particles,
      // По этим числам считается сила вихря (resolveDustDevilStrength) — и
      // здесь, и в сцене, которой нужна слышимость его звука
      growMs: params.growMs,
      fadeMs: params.fadeMs,
      floor,
      floorFade: params.basinFade,
      shadowSwayPeriodMs: randomIn(rng, params.shadow.swayPeriodMs),
      shadowSwayPhase: rng() * TWO_PI,
    };
  };

  /**
   * Размер вихря — доля полного: плавно растет с рождения и плавно сходит на
   * нет к концу, той же кривой, что и плотность
   */
  const resolveDevilScale = (group, timeMs) => (
    lerp(params.growFromScale, 1, resolveAppear(group, timeMs))
    * lerp(params.fadeToScale, 1, resolveVanish(group, timeMs))
  );

  const resolvePlumePose = (group, particle, elapsedMs, out) => {
    const {plume} = params;
    // Круг жизни клуба: на каждом круге он рождается там, где в этот миг вихрь
    const since = elapsedMs + particle.phaseMs;
    const cycle = Math.floor(since / plumeLifeMs);
    const ageMs = since - cycle * plumeLifeMs;
    const bornAtMs = elapsedMs - ageMs;
    // Клуб, чья очередь пришлась на время до рождения вихря, еще не родился; а
    // после конца вихря новым клубам рождаться не под чем — шлейф доживает
    if (!(bornAtMs > 0) || !(bornAtMs < group.whirlMs)) {
      return writeHiddenPose(out);
    }
    const age = ageMs / plumeLifeMs;
    const {x, y} = resolveDevilPoint(group, bornAtMs);
    // Клуб помнит, каким вихрь был в миг его рождения: легший клуб не сжимается
    // и не тает вместе с вихрем — лежит где лег и живет сам по себе
    const bornSize = group.coreSize * resolveDevilScale(group, bornAtMs);
    const jitterAngle = hash01(particle.seed, cycle) * TWO_PI;
    const jitter = bornSize * plume.jitterShare * hash01(particle.seed + 0.37, cycle);
    // Висящую пыль понемногу сносит ветер матча
    const drift = (hexSize * plume.driftFactor * ageMs) / 1000;
    const puffX = x + Math.cos(jitterAngle) * jitter + wind.x * drift;
    const puffY = y + Math.sin(jitterAngle) * jitter + wind.y * drift;
    // Пыль расходится быстро, пока свежа, и все медленнее потом
    const size = bornSize * lerp(plume.sizeShare.from, plume.sizeShare.to, age ** plume.spreadPower);
    // Чем слабее был вихрь, тем меньше пыли он поднял: клубы, рожденные, пока
    // он рос или сходил на нет, бледнее
    const alpha = plume.alpha
      * smoothstep(0, plume.riseMs, ageMs) * (1 - age) ** plume.fadePower
      * resolveAppear(group, bornAtMs) * resolveVanish(group, bornAtMs)
      * resolveOvalFade(group.floor, puffX, puffY, group.floorFade);
    const turn = toRadians(plume.turnDeg) * (hash01(particle.seed + 0.71, cycle) < 0.5 ? 1 : -1);
    return writePose(out, puffX, puffY, alpha, size, size, jitterAngle + turn * age);
  };

  const resolveWispPose = (group, particle, elapsedMs, x, y, size, presence, out) => {
    const {wisps} = params;
    // Круг жизни пряди: от рождения у тела до внешнего радиуса, потом — заново
    const since = elapsedMs + particle.phaseMs;
    const cycle = Math.floor(since / particle.cycleMs);
    const life = since / particle.cycleMs - cycle;
    // От середины вихря прядь уходит ровно, а по кругу вихрь несет ее тем
    // медленнее, чем она дальше: у тела прядь кружит быстро, у края — плывет.
    // Какую долю своих оборотов прядь уже сделала — по логарифму расстояния
    const radiusShare = lerp(wisps.radiusShare.from, wisps.radiusShare.to, life);
    const turnShare = Math.log(radiusShare / wisps.radiusShare.from) / wispSpiralSpan;
    const angle = hash01(particle.seed, cycle) * TWO_PI + group.spinDirection * TWO_PI * particle.turns * turnShare;
    // Плотность — по расстоянию до середины вихря: у тела полная, к внешнему
    // радиусу сходит на нет. Рождается прядь не скачком: проступает на первых
    // шагах от тела, под его плотным пятном
    const alpha = wisps.alpha * presence * smoothstep(0, wisps.riseShare, life)
      * (1 - smoothstep(wisps.fadeFromShare, wisps.radiusShare.to, radiusShare));
    const radius = size * radiusShare;
    return writePose(
      out, x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, alpha,
      // По пути прядь вытягивается и становится тоньше
      size * lerp(wisps.lengthShare.from, wisps.lengthShare.to, life),
      size * lerp(wisps.thicknessShare.from, wisps.thicknessShare.to, life),
      // Дуга кадра выгнута наружу: нижней стороной кадр смотрит на середину
      // вихря, а его длина повернута от касательной на угол спирали — тот конец
      // пряди, что впереди по вращению, дальше от вихря
      angle + Math.PI / 2 - group.spinDirection * particle.pitch
    );
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    if (!(elapsedMs > 0) || !(elapsedMs < group.durationMs)) {
      return writeHiddenPose(out);
    }
    if (particle.kind === PLUME) {
      return resolvePlumePose(group, particle, elapsedMs, out);
    }
    // Самого вихря уже нет: группа доживает одним шлейфом
    if (!(elapsedMs < group.whirlMs)) {
      return writeHiddenPose(out);
    }

    // Плотность тела, прядей и тени ведет сила вихря — та же, что и его звук
    const presence = resolveDustDevilStrength(group, elapsedMs);
    const {x, y} = resolveDevilPoint(group, elapsedMs);
    // Ядро, доли, пряди и тень сжаты одинаково — вокруг середины вихря на земле
    const size = group.coreSize * resolveDevilScale(group, elapsedMs);
    // Вращение одно на всех: на столько радиан повернулось бы то, что делает
    // оборот за миллисекунду
    const spin = group.spinDirection * TWO_PI * elapsedMs;

    if (particle.kind === SHADOW) {
      const {shadow} = params;
      const length = size * shadow.lengthShare;
      // Столб покачивается: тень ходит вокруг основания вихря
      const angle = shadowAngle + toRadians(shadow.swayDeg)
        * Math.sin((TWO_PI * elapsedMs) / group.shadowSwayPeriodMs + group.shadowSwayPhase);
      // Основание воронки в кадре — не у его края: середина кадра отстоит от
      // вихря так, чтобы под вихрем оказалось именно основание
      const centreGap = length * shadowCentreShare;
      return writePose(
        out, x + Math.cos(angle) * centreGap, y + Math.sin(angle) * centreGap,
        shadow.alpha * presence, length * shadowFrameScale, size * shadow.widthShare, angle
      );
    }
    if (particle.kind === WISP) {
      return resolveWispPose(group, particle, elapsedMs, x, y, size, presence, out);
    }
    if (particle.kind === LOBE) {
      // Доля кружит вокруг середины вихря и вращается сама
      const angle = particle.orbitPhase + spin / particle.orbitPeriodMs;
      const orbit = size * particle.orbitShare;
      const lobeSize = size * particle.sizeShare;
      return writePose(
        out, x + Math.cos(angle) * orbit, y + Math.sin(angle) * orbit, particle.alpha * presence,
        lobeSize, lobeSize, particle.spinPhase + spin / particle.spinPeriodMs
      );
    }
    // Ядро — в середине вихря: вращается, а его ширина и высота дышат порознь
    return writePose(
      out, x, y, params.core.alpha * presence,
      size * (1 + sumWaves(particle.widthWaves, elapsedMs)), size * (1 + sumWaves(particle.heightWaves, elapsedMs)),
      particle.spinPhase + spin / params.core.spinPeriodMs
    );
  };

  return [{
    tag: DUST_DEVIL_TAG,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups},
    buildGroup,
    resolvePose,
  }];
};

const dustDevil = {
  kind: DUST_DEVIL_KIND,
  textures: [FIELD_BIOME_TEXTURES.PUFF, FIELD_BIOME_TEXTURES.RIM_SEGMENT, FIELD_BIOME_TEXTURES.FUNNEL],
  createTracks,
};

export default dustDevil;
